/**
 * Drill-through view stack.
 *
 * When an operator navigates list → detail (and detail → nested detail), the
 * shell needs a coherent "back" target and breadcrumb context — but a deep
 * link opened cold (e.g. a notification linking straight to an entity detail)
 * has no in-app history to pop. react-router's `navigate(-1)` would bounce the
 * operator out of the app entirely in that case.
 *
 * This module keeps a lightweight, per-(tenant,user) stack of visited
 * module-view locations persisted to sessionStorage. ModuleView pushes each
 * location it renders; the back control pops to the previous distinct entry
 * (or, when the stack is shallow because the view was deep-linked, falls back
 * to the parent list view of the current module). Scoping by tenant+user keeps
 * one operator's drill-through trail from leaking into another's on a shared
 * workstation, and clears naturally on tab close (sessionStorage).
 */

export interface ViewStackEntry {
  /** Full router path, e.g. `/inventory/transactions/abc-123`. */
  path: string;
  /** Human label for the breadcrumb / back tooltip. */
  label: string;
  moduleId?: string;
  viewId?: string;
  entityId?: string;
}

const KEY_PREFIX = 'tyndo.viewStack';
const MAX_DEPTH = 25;

function storageKey(scope: { tenantId?: string | null; userId?: string | null }): string {
  const t = scope.tenantId || 'no-tenant';
  const u = scope.userId || 'anon';
  return `${KEY_PREFIX}:${t}:${u}`;
}

function read(key: string): ViewStackEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ViewStackEntry[]) : [];
  } catch {
    return [];
  }
}

function write(key: string, stack: ViewStackEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(stack.slice(-MAX_DEPTH)));
  } catch {
    /* sessionStorage may be unavailable (private mode / quota) — degrade to in-memory only. */
  }
}

export interface ViewStackScope {
  tenantId?: string | null;
  userId?: string | null;
}

/**
 * Record a visited location. Idempotent for the most-recent entry (a re-render
 * of the same path doesn't grow the stack), and pops forward history when the
 * operator navigates back to an earlier entry (so re-drilling doesn't leave a
 * stale forward trail).
 */
export function pushViewLocation(scope: ViewStackScope, entry: ViewStackEntry): ViewStackEntry[] {
  const key = storageKey(scope);
  const stack = read(key);
  const top = stack[stack.length - 1];
  if (top?.path === entry.path) {
    // Same location re-rendered — refresh its label but don't grow the stack.
    stack[stack.length - 1] = { ...top, ...entry };
    write(key, stack);
    return stack;
  }
  // If the operator navigated back to an entry already in the stack, truncate
  // the forward history at that point before pushing the new branch.
  const existingIdx = stack.findIndex((e) => e.path === entry.path);
  if (existingIdx >= 0) {
    const truncated = stack.slice(0, existingIdx + 1);
    truncated[truncated.length - 1] = { ...truncated[truncated.length - 1], ...entry };
    write(key, truncated);
    return truncated;
  }
  const next = [...stack, entry];
  write(key, next);
  return next;
}

/** The entry immediately beneath the current top (the back target), if any. */
export function getParentLocation(scope: ViewStackScope): ViewStackEntry | undefined {
  const stack = read(storageKey(scope));
  return stack.length >= 2 ? stack[stack.length - 2] : undefined;
}

export function getStack(scope: ViewStackScope): ViewStackEntry[] {
  return read(storageKey(scope));
}

/** Clear the stack for a scope (e.g. on logout / tenant switch). */
export function clearViewStack(scope: ViewStackScope): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(storageKey(scope));
  } catch {
    /* ignore */
  }
}
